import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ConfigService, StoreService } from 'app/services';
import { Utils } from 'app/utils';

enum GeometryType {
    Polygon = 1,
    Circle = 2
}

@Component({
    selector: 'partner-mart-locations-edit-group',
    templateUrl: './partner-mart-locations-edit-group.component.html',
    styleUrls: ['./partner-mart-locations-edit-group.component.scss']
})
export class PartnerMartLocationsEditGroupComponent implements OnInit, OnDestroy {
    public geometryType = GeometryType;

    @Input()
    public groupId: number = 0;

    @Input()
    public onSave: () => void = () => { };

    @Input()
    public onHide: () => void = () => { };

    public group: any = {};

    public geometryTypeId: GeometryType = GeometryType.Polygon;

    private map!: google.maps.Map;
    private drawingManager!: google.maps.drawing.DrawingManager;
    private overlayCompleteListener: google.maps.MapsEventListener | undefined = undefined;
    private zoomChangedListener: google.maps.MapsEventListener | undefined = undefined;
    private groupPolygon: google.maps.Polygon | undefined = undefined;
    private groupCircle: google.maps.Circle | undefined = undefined;

    constructor(
        private messageService: MessageService,
        public configService: ConfigService,
        private storeService: StoreService) {
    }

    ngOnInit(): void {
        const dto = {
            GroupId: this.groupId
        };

        this.storeService.getStoreGroupForEdit(dto, this.messageService)
            .then(data => {
                if (!data) {
                    return;
                }

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.group = data;

                if (this.group.Circle) {
                    this.geometryTypeId = GeometryType.Circle;
                }

                this.initMap();
            });
    }

    ngOnDestroy() {
        if (this.overlayCompleteListener) {
            google.maps.event.removeListener(this.overlayCompleteListener);
        }

        if (this.zoomChangedListener) {
            google.maps.event.removeListener(this.zoomChangedListener);
        }
    }

    // Init map
    private initMap(): void {
        const options = Object.assign({}, Utils.getDefaultMapOptions(), {
            mapTypeControl: false, // Remove "map satellite" and other options
            zoomControl: false,
            streetViewControl: false
        });

        // Create map
        if (!this.map) {
            this.map = new google.maps.Map(document.getElementById('editGroupMap') as HTMLElement, options);

            const drawingModes: google.maps.drawing.OverlayType[] = [];

            if (this.geometryTypeId === GeometryType.Polygon) {
                drawingModes.push(google.maps.drawing.OverlayType.POLYGON);
            }
            else if (this.geometryTypeId === GeometryType.Circle) {
                drawingModes.push(google.maps.drawing.OverlayType.CIRCLE);
            }

            // Create drawingManager
            this.drawingManager = new google.maps.drawing.DrawingManager();
            this.drawingManager.setOptions({
                drawingMode: null, //google.maps.drawing.OverlayType.POLYGON,
                drawingControl: true, // default drawingMode buttons
                drawingControlOptions: {
                    position: google.maps.ControlPosition.TOP_LEFT,
                    drawingModes
                },
                polygonOptions: {
                    fillColor: '#FF0000',
                    fillOpacity: 0.35,
                    strokeColor: '#FF0000',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    editable: false,
                    zIndex: 1
                },
                circleOptions: {
                    fillColor: '#FF0000',
                    fillOpacity: 0.35,
                    strokeColor: '#FF0000',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    editable: false,
                    zIndex: 1
                }
            });

            // Add drawingManager to map
            this.drawingManager.setMap(this.map);

            // Add drawingManager event
            this.overlayCompleteListener = google.maps.event.addListener(this.drawingManager, 'overlaycomplete', (evt: any) => {
                // Turn off drawing mode
                this.drawingManager.setDrawingMode(null);

                // Delete previuos shape
                if (this.groupPolygon) {
                    this.groupPolygon.setMap(null);
                }
                if (this.groupCircle) {
                    this.groupCircle.setMap(null);
                }

                this.groupPolygon = undefined;
                this.groupCircle = undefined;

                this.group.Polygon = null;
                this.group.Circle = null;

                // Polygon was drawn
                if (evt.type === google.maps.drawing.OverlayType.POLYGON) {
                    this.groupPolygon = evt.overlay;

                    this.group.Polygon = [];

                    const latLngArray = evt.overlay.getPath().getArray();

                    for (const latLng of latLngArray) {
                        this.group.Polygon.push({
                            Lat: latLng.lat(),
                            Lng: latLng.lng()
                        });
                    }
                }
                // Circle was drawn
                else if (evt.type === google.maps.drawing.OverlayType.CIRCLE) {
                    this.groupCircle = evt.overlay;

                    this.group.Circle = {
                        Lat: evt.overlay.getCenter().lat(),
                        Lng: evt.overlay.getCenter().lng(),
                        Radius: evt.overlay.getRadius()
                    };
                }
            });

            // Zoom changed
            this.zoomChangedListener = google.maps.event.addListener(this.map, 'zoom_changed', () => {
                // Redraw locations
                //this.showStoresOnMap();
            });
        }

        // Show group on map
        this.showGroupOnMap();

        // Show group stores on map
        //this.showStoresOnMap();
    }

    // Show group on map
    private showGroupOnMap(): void {
        if (this.groupPolygon) {
            this.groupPolygon.setMap(null);
            this.groupPolygon = undefined;
        }

        if (this.groupCircle) {
            this.groupCircle.setMap(null);
            this.groupCircle = undefined;
        }

        // If group is polygon
        if (this.group.Polygon) {
            const bounds = new google.maps.LatLngBounds();
            const polygonCoords: any[] = [];

            for (const polygon of this.group.Polygon) {
                bounds.extend(new google.maps.LatLng(polygon.Lat, polygon.Lng));
                polygonCoords.push({ lat: polygon.Lat, lng: polygon.Lng });
            }

            this.groupPolygon = new google.maps.Polygon({
                paths: polygonCoords,
                fillColor: '#FF0000',
                fillOpacity: 0.35,
                strokeColor: '#FF0000',
                strokeOpacity: 0.8,
                strokeWeight: 2
            });

            this.groupPolygon.setMap(this.map);

            // Fit group on map
            this.map.fitBounds(bounds);
        }
        // If group is circle
        else if (this.group.Circle) {
            this.groupCircle = new google.maps.Circle({
                radius: this.group.Circle.Radius,
                center: { lat: this.group.Circle.Lat, lng: this.group.Circle.Lng },
                fillColor: '#FF0000',
                fillOpacity: 0.35,
                strokeColor: '#FF0000',
                strokeOpacity: 0.8,
                strokeWeight: 2
            });

            this.groupCircle.setMap(this.map);

            // Fit group on map
            const bounds = this.groupCircle.getBounds();

            if (bounds) {
                this.map.fitBounds(bounds);
            }
        }
    }

    public onGeometryTypeClick(): void {
        let drawingMode: google.maps.drawing.OverlayType = google.maps.drawing.OverlayType.POLYGON;

        if (this.geometryTypeId === GeometryType.Circle) {
            drawingMode = google.maps.drawing.OverlayType.CIRCLE;
        }

        // Start drawing
        this.drawingManager.setDrawingMode(drawingMode);

        // Change drawing button
        this.drawingManager.setOptions({
            drawingControlOptions: {
                position: google.maps.ControlPosition.TOP_LEFT,
                drawingModes: [drawingMode]
            }
        });
    }

    public onSaveClick(): void {
        const dto = {
            GroupId: this.group.Id,
            Name: this.group.Name,
            Description: this.group.Description,
            Polygon: this.group.Polygon,
            Circle: this.group.Circle
        };

        this.storeService.updateStoreGroup(dto, this.messageService)
            .then(data => {
                if (!data) {
                    return;
                }

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.onSave();
            });
    }

    public onSideBarHide(): void {
        this.onHide();
    }
}
